import { AuthManager, CenterManager } from "@cuatroochenta/co-admin-react-library";
import Urls from "./Urls";
import Resumable from "resumablejs";
import { CustomResponse } from "../model/data/common";
import { Center } from "../model/domain/center";
import { ShareCenterOption } from "../model/domain/share-option";

export default class TaskBase {

    protected get selectedCenter() {
        return CenterManager.getSelectedCenter()
    }

    protected getRequestInfo() {
        return {
            method: 'GET',
            headers: {
                'Authorization': 'Bearer ' + AuthManager.getAuthToken()
            }
        };
    }

    public getUser() {
        const url = `${Urls.URL_GET_USER}`;

        const requestInfo = this.getRequestInfo();

        return fetch(url, requestInfo)
            .then(response => {
                if (!response.ok) {
                    return response.json().then((data) => {
                        throw new Error(data.message || 'Error al obtener la información del usuario');
                    });
                }
                return response.json();
            })
            .then(data => {
                return data;
            })
            .catch(error => {
                console.error('Error al obtener la información del usuario:', error);
                throw error;
            });
    }

    public syncUsers() {
        const selectedCenter = CenterManager.getSelectedCenter();
        const url = `${Urls.URL_SYNC_USERS}?center_id=${selectedCenter}`;

        const requestInfo = this.getRequestInfo();

        return fetch(url, requestInfo)
            .then(response => {
                if (!response.ok) {
                    return response.json().then((data) => {
                        throw new Error(data.message || 'Error al sincronizar la información del usuario');
                    });
                }
                return response.json();
            })
            .then(data => {
                return data;
            })
            .catch(error => {
                console.error('Error al sincronizar la información del usuario:', error);
                throw error;
            });
    }

    public syncDocuments() {
        const selectedCenter = CenterManager.getSelectedCenter();
        const url = `${Urls.URL_SYNC_DOCUMENTS}?center_id=${selectedCenter}`;

        const requestInfo = this.getRequestInfo();

        return fetch(url, requestInfo)
            .then(response => {
                if (!response.ok) {
                    return response.json().then((data) => {
                        throw new Error(data.message || 'Error al sincronizar los documentos');
                    });
                }
                return response.json();
            })
            .then(data => {
                return data;
            })
            .catch(error => {
                console.error('Error al sincronizar los documentos:', error);
                throw error;
            });
    }

    public async sendWithVideoChunked(url: string, postContent: { media?: File[] }) {
        return new Promise<void>((resolve, reject) => {
            if (!postContent.media || !postContent.media.length) {
                reject(new Error('No se ha seleccionado ningún vídeo.'));
                return;
            }

            // Calculate chunk size
            const mediaSize = postContent.media[0].size / (1024 * 1024)
            const chunkSize = (mediaSize > 500
                ? 20
                : 5
            ) * 1024 * 1024;

            const resumable = new Resumable({
                target: url,
                chunkSize,
                testChunks: false,
                fileParameterName: 'media',
                headers: this.getRequestInfo().headers,
                generateUniqueIdentifier: () => new Date().getTime().toString()
            });

            resumable.on('fileError', (_, message) => {
                console.error('Error al subir el vídeo:', message);
            });

            resumable.on('complete', () => {
                console.log('Todos los chunks han sido procesados.');
                resolve();
            });
    
            resumable.on('error', (message) => {
                console.error('Error general de Resumable.js:', message);
                reject(new Error(message));
            });

            resumable.on('fileAdded', (_) => {
                resumable.upload();
            });

            resumable.addFiles(postContent.media);
        });
    }

    public getVideo(mediaId: number): string {
        const token = AuthManager.getAuthToken();
        return `${Urls.URL_GET_VIDEOS}/${mediaId}?token=${token}`;
    }

    public getImage(mediaId: number): Promise<Blob | null> {
        const token = AuthManager.getAuthToken();
        const url = `${Urls.URL_GET_IMAGES}/${mediaId}?token=${token}`;

        return fetch(url)
            .then(response => {
                if (!response.ok) {
                    throw new Error('Error fetching media');
                }

                return response.blob();
            })
            .then(blob => {
                return blob;
            })
            .catch(error => {
                console.error('Error en la obtención del video:', error);
                return null; // Retorna null en caso de error
            });
    }

    public fetchMediaById(mediaId: string, mediaType: string): Promise<Blob | null> {
        const token = AuthManager.getAuthToken();

        let url = '';
        if (mediaType === 'images') {
            // Si es una imagen, obtenemos la imagen
            url = `${Urls.URL_GET_IMAGES}/${mediaId}?token=${token}`;
        } else if (mediaType === 'video') {
            // Si es un video, obtenemos el thumbnail en lugar del video
            url = `${Urls.URL_GET_THUMBNAILS}/${mediaId}?token=${token}`;
        }

        // Realizamos la solicitud para obtener el recurso (imagen o thumbnail)
        return fetch(url)
            .then(response => {
                if (!response.ok) {
                    throw new Error('Error fetching media');
                }

                // Verifica el tipo de contenido de la respuesta
                const contentType = response.headers.get('Content-Type');

                if (!contentType?.startsWith('image')) {
                    throw new Error('La respuesta no es una imagen, tipo recibido: ' + contentType);
                }

                return response.blob();
            })
            .then(blob => {
                return blob;
            })
            .catch(error => {
                console.error('Error en la obtención de la imagen o thumbnail:', error);
                return null; // Retorna null en caso de error
            });
    }

    public getMediaUrlById(mediaId: string, mediaType: string): string {
        const token = AuthManager.getAuthToken();

        let url = '';
        if (mediaType === 'images') {
            // Si es una imagen, obtenemos la imagen
            url = `${Urls.URL_GET_IMAGES}/${mediaId}?token=${token}`;
        } else if (mediaType === 'video') {
            // Si es un video, obtenemos el thumbnail en lugar del video
            url = `${Urls.URL_GET_THUMBNAILS}/${mediaId}?token=${token}`;
        }

        return url;
    }

    public getCenters(): Promise<CustomResponse<Center[]>> {
        return fetch(Urls.URL_GET_CENTERS, this.getRequestInfo())
            .then(response => response.json())
            .then(data => {
                return data;
            })
            .catch(error => {
                console.error('Error fetching news:', error);
                throw error;
            });
    }

    public getFormDataFromObject(obj: object): FormData {
        const formData = new FormData()

        const presentValues = Object
            .entries(obj)
            .filter(([_, value]) => value != null && `${value}`.length)

        for (const [key, value] of presentValues) {
            formData.append(
                key,
                Array.isArray(value)
                    ? JSON.stringify(value)
                    : value
            )
        }

        return formData
    }

    public getShareOptions(): Promise<ShareCenterOption[]> {
        const url = Urls.URL_GET_SHARE_OPTIONS;

        return fetch(url, this.getRequestInfo())
            .then(response => response.json())
            .then((response: CustomResponse<ShareCenterOption[]>) => {
                if (!response.success) {
                    throw new Error(
                        Array.isArray(response.error)
                            ? response.error[0]
                            : response.error
                    );
                }
            
                return response.data!
            })
            .catch(error => {
                console.error('Error fetching share options:', error);
                throw error;
            });
    }
}